import React, { useState, useCallback } from 'react';
import {
  WorksheetItem,
  WorksheetItemType,
  ITEM_TYPES,
  UNITS,
  VAT_RATES,
  calculateItemTotal,
  calculateWorksheetTotals,
} from '../../types/worksheet';
import {
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';

interface WorksheetItemsProps {
  items: WorksheetItem[];
  onChange: (items: WorksheetItem[]) => void;
  readOnly?: boolean;
}

const WorksheetItems: React.FC<WorksheetItemsProps> = ({ items, onChange, readOnly = false }) => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(true);

  const addItem = useCallback((type: WorksheetItemType) => {
    if (type === 'header') {
      const newItem: WorksheetItem = {
        id: `item_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        type: 'header',
        name: '',
        quantity: 0,
        unit: '',
        unitPrice: 0,
        vatRate: 27,
        totalNet: 0,
        totalGross: 0,
        order: items.length,
      };
      onChange([...items, newItem]);
    } else {
      const newItem: WorksheetItem = {
        id: `item_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
        type,
        name: '',
        quantity: 1,
        unit: type === 'labor' ? 'ó' : type === 'travel' ? 'km' : 'db',
        unitPrice: 0,
        vatRate: 27,
        totalNet: 0,
        totalGross: 0,
        order: items.length,
      };
      onChange([...items, newItem]);
    }
  }, [items, onChange]);

  const updateItem = useCallback((id: string, field: keyof WorksheetItem, value: any) => {
    const updated = items.map(item => {
      if (item.id !== id) return item;
      const updatedItem = { ...item, [field]: value };
      if (item.type !== 'header') {
        const total = calculateItemTotal(
          updatedItem.quantity,
          updatedItem.unitPrice,
          updatedItem.vatRate,
          updatedItem.discount,
          updatedItem.discountType
        );
        updatedItem.totalNet = total.net;
        updatedItem.totalGross = total.gross;
      }
      return updatedItem;
    });
    onChange(updated);
  }, [items, onChange]);

  const removeItem = useCallback((id: string) => {
    onChange(items.filter(item => item.id !== id));
  }, [items, onChange]);

  const moveItem = useCallback((id: string, direction: 'up' | 'down') => {
    const index = items.findIndex(item => item.id === id);
    if (index < 0) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    const reordered = [...items];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    reordered.forEach((item, idx) => { item.order = idx; });
    onChange(reordered);
  }, [items, onChange]);

  const totals = calculateWorksheetTotals(items);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div
        className="px-3 py-2 sm:px-4 sm:py-3 flex items-center justify-between cursor-pointer bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700 hover:from-indigo-700 hover:to-purple-700 dark:hover:from-indigo-800 dark:hover:to-purple-800 transition-all"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center space-x-2">
          <h3 className="text-xs sm:text-sm font-semibold text-white">{t('worksheets.items.title')}</h3>
          <span className="text-[10px] sm:text-xs text-white/80">({items.filter(i => i.type !== 'header').length})</span>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="text-right whitespace-nowrap">
            <span className="hidden sm:inline text-[10px] sm:text-xs text-white/80">{t('worksheets.items.net')}</span>
            <span className="hidden sm:inline ml-1 text-xs sm:text-sm font-bold text-white">{totals.totalNet.toLocaleString('hu-HU')} Ft</span>
            <span className="ml-2 text-[10px] sm:text-xs text-white/80">{t('worksheets.items.gross')}</span>
            <span className="ml-1 text-xs sm:text-sm font-bold text-white">{totals.totalGross.toLocaleString('hu-HU')} Ft</span>
          </div>
          {expanded ? <ChevronUpIcon className="w-4 h-4 text-white/80" /> : <ChevronDownIcon className="w-4 h-4 text-white/80" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Items table */}
          {items.length > 0 && (
            <div className="hidden sm:block overflow-x-auto -mx-2 px-2">
              <table className="w-full min-w-[640px] text-xs sm:text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900/50 text-xs text-gray-500 dark:text-gray-400">
                    <th className="px-2 py-2 text-left w-8"></th>
                    <th className="px-2 py-2 text-left">{t('worksheets.items.name')}</th>
                    <th className="px-2 py-2 text-right w-20">{t('worksheets.items.quantity')}</th>
                    <th className="px-2 py-2 text-left w-20">{t('worksheets.items.unit')}</th>
                    <th className="px-2 py-2 text-right w-24">{t('worksheets.items.unitPrice')}</th>
                    <th className="px-2 py-2 text-center w-16">{t('worksheets.items.vat')}</th>
                    <th className="px-2 py-2 text-right w-24">{t('worksheets.items.netTotal')}</th>
                    <th className="px-2 py-2 text-right w-24">{t('worksheets.items.grossTotal')}</th>
                    {!readOnly && <th className="px-2 py-2 w-20"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {items.map((item) => (
                    <tr key={item.id} className={item.type === 'header' ? 'bg-gray-50 dark:bg-gray-900/30' : ''}>
                      <td className="px-2 py-2 text-center">
                        <span className="text-sm sm:text-lg" title={ITEM_TYPES.find(t => t.value === item.type)?.label}>
                          {ITEM_TYPES.find(t => t.value === item.type)?.icon}
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={item.name}
                          onChange={e => updateItem(item.id, 'name', e.target.value)}
                          disabled={readOnly}
                          placeholder={item.type === 'header' ? t('worksheets.items.headerPlaceholder') : t('worksheets.items.namePlaceholder')}
                          className={`crm-input-raw w-full bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-blue-400 rounded px-1 py-0.5 ${
                            item.type === 'header'
                              ? 'font-bold text-gray-800 dark:text-gray-200'
                              : 'text-gray-700 dark:text-gray-300'
                          } ${readOnly ? 'cursor-default' : ''}`}
                        />
                      </td>
                      {item.type === 'header' ? (
                        <td colSpan={6}></td>
                      ) : (
                        <>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={e => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                              disabled={readOnly}
                              className="crm-input-raw w-full text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-blue-400 rounded px-1 py-0.5 text-gray-700 dark:text-gray-300"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <select
                              value={item.unit}
                              onChange={e => updateItem(item.id, 'unit', e.target.value)}
                              disabled={readOnly}
                              className="crm-input-raw w-full bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-blue-400 rounded px-1 py-0.5 text-gray-700 dark:text-gray-300 text-xs"
                            >
                              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="number"
                              value={item.unitPrice}
                              onChange={e => updateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                              disabled={readOnly}
                              className="crm-input-raw w-full text-right bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-blue-400 rounded px-1 py-0.5 text-gray-700 dark:text-gray-300"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <select
                              value={item.vatRate}
                              onChange={e => updateItem(item.id, 'vatRate', parseInt(e.target.value))}
                              disabled={readOnly}
                              className="crm-input-raw w-full text-center bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-blue-400 rounded px-1 py-0.5 text-gray-700 dark:text-gray-300 text-xs"
                            >
                              {VAT_RATES.map(v => <option key={v} value={v}>{v}%</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-2 text-right text-gray-700 dark:text-gray-300 font-medium">
                            {item.totalNet.toLocaleString('hu-HU')}
                          </td>
                          <td className="px-2 py-2 text-right text-gray-700 dark:text-gray-300 font-medium">
                            {item.totalGross.toLocaleString('hu-HU')}
                          </td>
                        </>
                      )}
                      {!readOnly && (
                        <td className="px-2 py-2">
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => moveItem(item.id, 'up')}
                              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              <ChevronUpIcon className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => moveItem(item.id, 'down')}
                              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              <ChevronDownIcon className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => removeItem(item.id)}
                              className="p-1 text-red-400 hover:text-red-600"
                            >
                              <TrashIcon className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-900/50 font-bold text-gray-800 dark:text-gray-200">
                    <td colSpan={6} className="px-2 py-2 text-right">{t('worksheets.items.total')}</td>
                    <td className="px-2 py-2 text-right">{totals.totalNet.toLocaleString('hu-HU')} Ft</td>
                    <td className="px-2 py-2 text-right">{totals.totalGross.toLocaleString('hu-HU')} Ft</td>
                    {!readOnly && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Mobil: kártyás tétellista */}
          {items.length > 0 && (
            <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {items.map((item) => (
                <div key={item.id} className={`px-3 py-2.5 ${item.type === 'header' ? 'bg-gray-50 dark:bg-gray-900/30' : ''}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-base flex-shrink-0" title={ITEM_TYPES.find(t => t.value === item.type)?.label}>{ITEM_TYPES.find(t => t.value === item.type)?.icon}</span>
                    <input
                      type="text"
                      value={item.name}
                      onChange={e => updateItem(item.id, 'name', e.target.value)}
                      disabled={readOnly}
                      placeholder={item.type === 'header' ? t('worksheets.items.headerPlaceholder') : t('worksheets.items.namePlaceholder')}
                      className={`flex-1 min-w-0 text-sm ${readOnly ? 'crm-input-raw bg-transparent border-0 px-0 py-0' : 'crm-input-compact rounded-md border border-[var(--crm-border)] bg-[var(--crm-surface)] px-2'} ${item.type === 'header' ? 'font-bold' : ''}`}
                    />
                    {!readOnly && (
                      <div className="flex items-center flex-shrink-0">
                        <button type="button" onClick={() => moveItem(item.id, 'up')} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><ChevronUpIcon className="w-4 h-4" /></button>
                        <button type="button" onClick={() => moveItem(item.id, 'down')} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><ChevronDownIcon className="w-4 h-4" /></button>
                        <button type="button" onClick={() => removeItem(item.id)} className="p-1 text-red-400 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                  {item.type !== 'header' && (
                    <div className="mt-1.5 grid grid-cols-4 gap-1.5 text-xs">
                      <label className="min-w-0">
                        <span className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">{t('worksheets.items.quantity')}</span>
                        {readOnly ? <span className="block h-[30px] leading-[30px] truncate">{item.quantity}</span> : (
                          <input type="number" value={item.quantity} onChange={e => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)} className="crm-input-raw crm-input-compact w-full rounded-md border border-[var(--crm-border)] bg-[var(--crm-surface)] px-1.5 text-right" />
                        )}
                      </label>
                      <label className="min-w-0">
                        <span className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">{t('worksheets.items.unit')}</span>
                        {readOnly ? <span className="block h-[30px] leading-[30px] truncate">{item.unit}</span> : (
                          <select value={item.unit} onChange={e => updateItem(item.id, 'unit', e.target.value)} className="crm-input-raw crm-input-compact w-full rounded-md border border-[var(--crm-border)] bg-[var(--crm-surface)] px-1">
                            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        )}
                      </label>
                      <label className="min-w-0">
                        <span className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">{t('worksheets.items.unitPrice')}</span>
                        {readOnly ? <span className="block h-[30px] leading-[30px] truncate text-right">{item.unitPrice.toLocaleString('hu-HU')}</span> : (
                          <input type="number" value={item.unitPrice} onChange={e => updateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)} className="crm-input-raw crm-input-compact w-full rounded-md border border-[var(--crm-border)] bg-[var(--crm-surface)] px-1.5 text-right" />
                        )}
                      </label>
                      <label className="min-w-0">
                        <span className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">{t('worksheets.items.vat')}</span>
                        {readOnly ? <span className="block h-[30px] leading-[30px] truncate">{item.vatRate}%</span> : (
                          <select value={item.vatRate} onChange={e => updateItem(item.id, 'vatRate', parseInt(e.target.value))} className="crm-input-raw crm-input-compact w-full rounded-md border border-[var(--crm-border)] bg-[var(--crm-surface)] px-1">
                            {VAT_RATES.map(v => <option key={v} value={v}>{v}%</option>)}
                          </select>
                        )}
                      </label>
                      <div className="col-span-4 flex justify-between text-xs pt-1">
                        <span className="text-gray-500 dark:text-gray-400">{t('worksheets.items.netTotal')}: <b className="text-gray-800 dark:text-gray-200">{item.totalNet.toLocaleString('hu-HU')} Ft</b></span>
                        <span className="text-gray-500 dark:text-gray-400">{t('worksheets.items.grossTotal')}: <b className="text-gray-800 dark:text-gray-200">{item.totalGross.toLocaleString('hu-HU')} Ft</b></span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="px-3 py-2 flex justify-between text-xs font-bold bg-gray-50 dark:bg-gray-900/50 text-gray-800 dark:text-gray-200">
                <span>{t('worksheets.items.total')}</span>
                <span>{totals.totalNet.toLocaleString('hu-HU')} Ft / {totals.totalGross.toLocaleString('hu-HU')} Ft</span>
              </div>
            </div>
          )}

          {/* Add buttons */}
          {!readOnly && (
            <div className="px-3 py-2 flex flex-wrap items-center gap-1.5 border-t border-gray-100 dark:border-gray-700">
              {ITEM_TYPES.map(type => (
                <button
                  key={type.value}
                  onClick={() => addItem(type.value)}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  <PlusIcon className="w-3 h-3 mr-1" />
                  {type.icon} {type.label}
                </button>
              ))}
            </div>
          )}

          {items.length === 0 && !readOnly && (
            <div className="px-4 py-6 text-center text-sm text-gray-400">
              {t('worksheets.items.empty')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorksheetItems;
